import type {
  DataGridColumnOrderState,
  DataGridColumnPinningState,
  TableFilterState,
  TableSortingState,
} from '@ibsys/kit';
import { useCallback, useMemo } from 'react';
import {
  type SearchConversationDownloadRequest,
  type SearchDialogsPage,
  type SearchQueryRequest,
  useDownloadConversationsSearch,
} from '@/entities/search';
import type { ExpertReportSettings } from '@/entities/settings';
import type { FileDownloadFormat } from '@/shared/lib';
import { orderColumnIdsAsInTable, orderExpertReportSettingsAsInTable } from './columnsOrder';
import { ATTRIBUTE_COLUMN_PREFIX } from './constants';
import type { DialogMainColumn } from './types';

/** Собирает срез выборки без постраничности (тот же путь, что и запрос списка). */
type BuildRequestParams = (params: {
  filters?: TableFilterState;
  sorting?: TableSortingState;
  dialogsPage?: SearchDialogsPage;
  splitPersonColumns?: boolean;
}) => Omit<SearchQueryRequest, 'limit' | 'offset'>;

/** Параметры выгрузки списка диалогов файлом */
type Params = {
  buildRequestParams: BuildRequestParams;
  /** Действующие фильтры выборки */
  filters: TableFilterState;
  /** Действующая сортировка выборки */
  sorting: TableSortingState;
  visibleAttributePaths: string[];
  visibleMainColumns: DialogMainColumn[];
  expertReportSearchParams: ExpertReportSettings[];
  /** Эффективный порядок колонок таблицы */
  columnOrder: DataGridColumnOrderState;
  /** Закрепление колонок таблицы */
  columnPinning: DataGridColumnPinningState;
};

/** Выгрузка списка диалогов файлом */
type Result = {
  onDownloadList: (format: FileDownloadFormat) => void;
  isDownloadPending: boolean;
};

/**
 * Выгрузка списка диалогов файлом. Файл повторяет то, что пользователь видит в списке: тот же срез,
 * что уходит в загрузку строк, но без постраничности — выгружается вся выборка, а не показанная её
 * часть.
 *
 * Колонки файла идут в том порядке, в каком стоят в таблице у пользователя: перестановка и
 * закрепление колонок доходят до файла. Списку такой порядок не нужен — колонки на экране
 * расставляет сам грид, — поэтому состав для выгрузки считается отдельно и запрос списка от
 * перестановки колонок не перезапрашивается. Порядок таблицы наследуется внутри каждой группы
 * колонок: файл всегда рисуется группами в порядке mainColumns → attributes → expertReport,
 * поэтому перестановка колонки из одной группы в другую в файле не отражается.
 *
 * Роли участников уходят раздельно: в файле «Клиент» и «Консультант» — свои колонки, как и в
 * таблице, а разводить их из объединённого состава, как это делает список, некому.
 */
export const useDialogDownload = ({
  buildRequestParams,
  filters,
  sorting,
  visibleAttributePaths,
  visibleMainColumns,
  expertReportSearchParams,
  columnOrder,
  columnPinning,
}: Params): Result => {
  const { mutate: downloadList, isPending: isDownloadPending } = useDownloadConversationsSearch();

  const downloadDialogsPage = useMemo(
    () => ({
      attributes: orderColumnIdsAsInTable(
        visibleAttributePaths.map((path) => `${ATTRIBUTE_COLUMN_PREFIX}${path}`),
        columnOrder,
        columnPinning,
      ).map((columnId) => columnId.slice(ATTRIBUTE_COLUMN_PREFIX.length)),
      mainColumns: orderColumnIdsAsInTable(visibleMainColumns, columnOrder, columnPinning) as DialogMainColumn[],
      expertReport: orderExpertReportSettingsAsInTable(expertReportSearchParams, columnOrder, columnPinning),
    }),
    [visibleAttributePaths, visibleMainColumns, expertReportSearchParams, columnOrder, columnPinning],
  );

  const onDownloadList = useCallback(
    (format: FileDownloadFormat) => {
      const params = buildRequestParams({
        filters,
        sorting,
        dialogsPage: downloadDialogsPage,
        splitPersonColumns: true,
      }) as Omit<SearchConversationDownloadRequest, 'entityType' | 'format'>;
      downloadList({ ...params, format });
    },
    [buildRequestParams, downloadList, filters, sorting, downloadDialogsPage],
  );

  return { onDownloadList, isDownloadPending };
};
